import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { PageContainer } from "@/components/ui/page-container";
import { MobilePageTitle } from "@/components/ui/mobile-page-title";
import { ScrollableContainer } from "@/components/ui/scrollbar";
import { WorkoutBlockView } from "@/components/athlete-profile/workouts/view";
import { useWorkoutBlock } from "@/queries/use-workout-blocks";
import {
  useAthleteNameBreadcrumb,
  useEntityNameBreadcrumb,
} from "@/hooks/use-breadcrumb/use-breadcrumb-overrides";
import { PROTECTED_ROUTES } from "@/utils/navigation";
import { guardWorkoutBlockId } from "@/utils/route-guards";

export const Route = createFileRoute(
  "/(protected)/athletes/$athleteId/workout-blocks/$workoutBlockId/",
)({
  beforeLoad: ({ params }) => guardWorkoutBlockId(params),
  component: RouteComponent,
});

function RouteComponent() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { athleteId, workoutBlockId } = Route.useParams();
  const numericAthleteId = Number(athleteId);
  const numericWorkoutBlockId = Number(workoutBlockId);

  const { data: workoutBlock } = useWorkoutBlock(
    numericAthleteId,
    numericWorkoutBlockId,
  );

  useAthleteNameBreadcrumb(numericAthleteId);
  useEntityNameBreadcrumb(workoutBlock?.name);

  const navigateToList = () =>
    navigate({
      to: PROTECTED_ROUTES.ATHLETE_DETAIL,
      params: { athleteId },
      search: { tab: "workouts" },
    });

  return (
    <div className="flex h-full flex-col px-6 py-5">
      <MobilePageTitle>
        {workoutBlock?.name ?? t("pages:profile.tabs.workouts")}
      </MobilePageTitle>
      <PageContainer
        className="flex min-h-0 flex-1 flex-col p-0 md:m-0 lg:mb-0"
        internalScroll
      >
        <ScrollableContainer>
          <div className="px-2 pt-6 pb-10 sm:px-6 xl:px-12">
            <WorkoutBlockView
              athleteId={numericAthleteId}
              workoutBlockId={numericWorkoutBlockId}
              onEdit={() =>
                navigate({
                  to: PROTECTED_ROUTES.ATHLETE_WORKOUT_BLOCK_EDIT,
                  params: { athleteId, workoutBlockId },
                  replace: true,
                })
              }
              onDuplicate={() =>
                navigate({
                  to: PROTECTED_ROUTES.ATHLETE_WORKOUT_BLOCK_NEW,
                  params: { athleteId },
                  search: { duplicateFrom: numericWorkoutBlockId },
                })
              }
              onDelete={navigateToList}
              onAddPlan={() =>
                navigate({
                  to: PROTECTED_ROUTES.ATHLETE_WORKOUT_PLAN_NEW,
                  params: { athleteId },
                  search: { workoutBlockId: numericWorkoutBlockId },
                })
              }
            />
          </div>
        </ScrollableContainer>
      </PageContainer>
    </div>
  );
}
